import "./EssayProgressCoachMarkOverlay.scss";

import React, { useMemo } from "react";
import ReactDOM from "react-dom";
import { CSSTransition } from "react-transition-group";

import { useRect } from "@reach/rect";

import coachMarkContents from "../coachMarks/coachMarkContents";
import { OverlayCoachMarkID } from "../coachMarks/coachMarkListing";
import { ApproximateNextReviewSessionStatus } from "../reviewSchedule";
import { EssayProgress } from "../util/getProgressByEssay";
import EssayProgressOverlay from "./EssayProgressOverlay";
import useSizeClass from "./hooks/useSizeClass";
import { SpacedRepetitionSchedule } from "../spacedRepetition";

type Props = {
  targetElement: HTMLElement;
  interiorRef?: React.Ref<HTMLDivElement>;
  isPresented: boolean;
  coachMarkID: OverlayCoachMarkID;
  essayProgress: EssayProgress;
  approximateNextReviewSessionStatus: ApproximateNextReviewSessionStatus;
  hasApplicationPrompts: boolean;
  schedule: SpacedRepetitionSchedule;
};

const desktopScaleFactor = 0.84; // Matches the scale in SiteMenu.scss.

export default function EssayProgressCoachMarkOverlay(props: Props) {
  const {
    targetElement,
    interiorRef,
    isPresented,
    coachMarkID,
    essayProgress,
    approximateNextReviewSessionStatus,
    hasApplicationPrompts,
    schedule,
  } = props;

  const sizeClass = useSizeClass();
  const targetProxyRef = useMemo(() => ({ current: targetElement }), [
    targetElement,
  ]);
  const containerPosition = useRect(targetProxyRef) as DOMRect | null;

  return (
    containerPosition &&
    ReactDOM.createPortal(
      <CSSTransition
        in={isPresented}
        appear
        timeout={2000}
        classNames="EssayProgressCoachMarkOverlay"
      >
        <div>
          <div
            className="EssayProgressCoachMarkOverlay-Scrim"
            style={{
              background: `radial-gradient(circle at ${containerPosition.left +
                140}px ${containerPosition.top +
                130}px, transparent 200px, rgba(0,0,0,0.05) 360px, rgba(0,0,0,0.3) 657px)`,
            }}
          />
          <EssayProgressOverlay
            scaleFactor={sizeClass === "desktop" ? desktopScaleFactor : 1.0}
            targetElement={targetElement}
            ref={interiorRef}
            schedule={schedule}
          >
            {(nodeLocations, nodeWidth, nodeHeight) =>
              coachMarkContents[coachMarkID]({
                isDesktop: sizeClass === "desktop",
                hasApplicationPrompts,
                nodeLocations,
                nodeWidth,
                nodeHeight,
                progress: essayProgress,
                approximateNextReviewSessionStatus,
                schedule,
              })
            }
          </EssayProgressOverlay>
        </div>
      </CSSTransition>,
      document.body,
    )
  );
}
